import { useRef, type PointerEvent, type ReactNode } from 'react';
import { useReducedMotion } from '@/lib/useReducedMotion';

interface TiltCardProps {
  children: ReactNode;
  className?: string;
  /** Maximum rotation in degrees. */
  max?: number;
  style?: React.CSSProperties;
}

/**
 * A surface that leans towards the pointer and lights up where it is.
 *
 * Transforms are written straight to the node — a state update per pointermove
 * would re-render the subtree sixty times a second for a purely visual effect.
 * Disabled entirely under reduced motion and on touch, where there is no hover
 * to respond to.
 */
export function TiltCard({ children, className, max = 5, style }: TiltCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || reduced || e.pointerType !== 'mouse') return;

    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    el.style.setProperty('--mx', `${x * 100}%`);
    el.style.setProperty('--my', `${y * 100}%`);
    el.style.transform = `perspective(900px) rotateX(${(0.5 - y) * max}deg) rotateY(${(x - 0.5) * max}deg) translate3d(0,-3px,0)`;
  };

  const reset = () => {
    const el = ref.current;
    if (el) el.style.transform = '';
  };

  return (
    <div
      ref={ref}
      className={`tilt ${className ?? ''}`}
      style={style}
      onPointerMove={onMove}
      onPointerLeave={reset}
    >
      <div className="tilt__glow" aria-hidden="true" />
      {children}
    </div>
  );
}
